<template>
  <form @submit.prevent="onSubmit">
    <slot></slot>
    <ErrorBox v-if="errors.length && errorBox" :items="errors">
      <template #header>
        <Icon class="error-icon" name="error" fill></Icon>
        <span class="text-b14 color-system-danger text-medium"> لطفا موارد زیر را بررسی کنید و مجدد تلاش نمایید </span>
      </template>
    </ErrorBox>
    <slot name="footer"></slot>
  </form>
</template>

<script lang="ts" setup>
import Icon from '@/components/base/icon/index.vue';
import ErrorBox from '@/components/error-box/index.vue';
import { inject, provide, reactive, ref, watch } from 'vue';
import type { TheFromProps, ValidationResult, ValidFunction } from '@/types';

defineProps<TheFromProps & { errorMessage?: string[] | null }>();
const emit = defineEmits(['submit']);
const errors = ref<string[]>([]);

const errorText = ref<string>('');
const state = reactive({
  validateComponents: [] as Array<{ uid: string; validate: ValidFunction }>,
});

// Provide form methods
provide('form', { bind, unbind, validate });

// Method to bind a component
function bind(component: { uid: string; validate: ValidFunction }) {
  state.validateComponents.push(component);
}

// Method to unbind a component by its UID
function unbind(uid: string) {
  const index = state.validateComponents.findIndex((c) => c.uid === uid);
  if (index > -1) {
    state.validateComponents.splice(index, 1);
  }
}

// Method to validate all components
function validate(): boolean {
  let isValid = true;
  errors.value = [];
  for (const component of state.validateComponents) {
    const { valid, error }: ValidationResult = component.validate();
    if (!valid) {
      errors.value.push(error);
      isValid = false;
    }
  }
  return isValid;
}

const errorMessageInject = inject<{ value: string[] } | null>('errorMessages', null);
watch(
  () => errorMessageInject?.value?.length,
  (newErrorMessages, oldValue) => {
    const errorValue = oldValue && errorMessageInject?.value[oldValue];
    if (errorValue && !errors?.value.includes(errorValue)) {
      errorText.value = errorValue;
      errors.value.push(errorValue);
    }

    if (oldValue && (newErrorMessages ?? 0) < oldValue && errorText.value !== '') {
      errors.value = errors.value.filter((err) => err !== errorText.value);
    }
  },
  { immediate: true }
);

function onSubmit() {
  const valid = validate();
  if (valid) {
    emit('submit');
  }
}

defineExpose({ validate });
</script>
